import { Link } from "@tanstack/react-router";
import { ArrowRight, Box } from "lucide-react";
import { formatArticle } from "@/content/format";
import type { ProductSummary } from "@/content/schema";
import { fr } from "@/i18n/fr";

interface ProductCardProps {
	product: ProductSummary;
	/** The product that opens the catalogue: a wide tile, the text beside the photo on desktop. */
	featured?: boolean;
}

/**
 * A product of the catalogue, photo first: the photo sits with room around it in a rounded white
 * tile, the name below in the display face. No border; the tile is the card. The catalogue only
 * lists ready products, so the card carries no status.
 */
export function ProductCard({ product, featured }: ProductCardProps) {
	return (
		<Link
			to="/products/$slug"
			params={{ slug: product.slug }}
			className={`group flex no-underline text-fg hover:text-fg focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-4 ${featured ? "flex-col gap-4 md:flex-row md:items-end md:gap-8" : "flex-col gap-3"}`}
		>
			{/* IKEA photos are shot on white: the tile is white too, so the photo can sit inside it with
			    breathing room and no second colour ever shows. */}
			<div
				className={`relative overflow-hidden rounded-xl ${product.photo_url ? "photo-tile" : "bg-bg-sunken"} ${featured ? "aspect-[4/3] md:aspect-[16/10] md:flex-[3]" : "aspect-[4/5]"}`}
			>
				{product.photo_url ? (
					<img
						src={product.photo_url}
						alt=""
						loading="lazy"
						decoding="async"
						className={`size-full object-contain transition duration-500 ease-out group-hover:scale-[1.03] ${featured ? "p-8 md:p-12" : "p-5 md:p-6"}`}
					/>
				) : (
					<div className="grid size-full place-items-center text-fg-muted">
						<Box
							className="size-[30%] opacity-50"
							strokeWidth={0.9}
							aria-hidden="true"
						/>
					</div>
				)}
			</div>
			<div className={featured ? "md:flex-[2] md:pb-2" : ""}>
				{/* the full category, on a two-line block of fixed height so the name sits at the same
				    height on every tile; the article number gets its own line under the designation */}
				<p className="line-clamp-2 min-h-8 text-eyebrow text-fg-muted uppercase first-letter:uppercase">
					{product.category}
				</p>
				<h2
					className={`mt-1 font-display font-medium tracking-tight ${featured ? "text-[32px] leading-9 md:text-[44px] md:leading-[46px]" : "text-[22px] leading-7"}`}
				>
					{product.name}
				</h2>
				<p
					className={`text-fg-muted ${featured ? "mt-2 max-w-md text-base" : "text-[13px] leading-[18px]"}`}
				>
					{product.designation}
				</p>
				<p className="mt-1 font-mono text-fg-muted text-xs tabular-nums">
					{formatArticle(product.ikea_id)}
				</p>
				{product.generation_status === "ready" && (
					<span
						className={`mt-3 inline-flex items-center gap-2 font-medium text-accent text-sm ${featured ? "pill mt-5 border-accent bg-accent text-accent-fg shadow-none group-hover:bg-accent-strong" : "group-hover:underline group-hover:underline-offset-[3px]"}`}
					>
						{fr.openGuide}
						<ArrowRight className="size-4 transition group-hover:translate-x-0.5" />
					</span>
				)}
			</div>
		</Link>
	);
}
